import React, { useState } from 'react';
import OpenModalButton from "./OpenModalButton";
import SignupForm from "./SignupForm";
import { useNavigate } from "react-router-dom";

const AboutApp = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const handleRedirect = () => {
    navigate("/bible-reading");
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#30798a] text-white text-center py-16">
        <h1 className="text-4xl font-bold mb-4">Welcome to Bible Memos</h1>
        <p className="text-lg mb-6">Transform your Bible reading experience with interactive tools and personalized insights.</p>
        <button
          className="bg-white text-blue-600 font-semibold py-2 px-6 rounded shadow hover:bg-gray-200"
          onClick={handleRedirect}
        >
          Start Your Journey
        </button>
        {/* <img src="/images/logo-png.png" alt="App Screenshot" className="mx-auto my-5 rounded-lg shadow-lg max-w-md" /> */}
      </section>

      {/* Overview Section */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Discover Bible Memos</h2>
          <p className="text-gray-700 mb-6">
            <span className='font-semibold'>
              Bible Memos brings scripture to life. With advanced search, comparison tools, and memo-taking features, you can read deeply, write sermons effectively, and grow spiritually—all in one place.
            </span>



            {isExpanded && (
              <span className='text-left'>
              <p className="mt-4">
                Bible Memos revolutionizes the way you engage with scripture, bringing God’s Word to life like never before. Designed to meet the needs of both personal devotion and professional study, Bible Memos offers an unparalleled experience.
              </p>
            
              <p className="mt-4">
                Dive deeply into the Word with features that allow you to search across multiple Bible versions, compare translations side-by-side for richer understanding, and gain new insights into familiar passages. Whether you're searching for a single verse or exploring complex theological themes, the intuitive search engine makes finding relevant scriptures effortless.
              </p>
            
              <p className="mt-4">
                Take your Bible study further by adding personalized notes and reflections to specific verses, perfect for journaling your spiritual journey or preparing impactful sermons. Highlight and bookmark your favorite passages, ensuring that your most cherished scriptures are always within reach.
              </p>
            
              <p className="mt-4">
                For pastors, teachers, and leaders, the sermon writing tools provide a seamless way to craft, organize, and store sermons, all while staying connected to the biblical text. Bible Memos ensures your spiritual resources are well-organized and accessible.
              </p>
            
              <p className="mt-4">
                Designed to make scripture accessible anytime and anywhere, Bible Memos supports syncing across all your devices, so you’re never far from the tools you need to grow in faith. This powerful integration helps you stay connected to the Word, no matter where life takes you.
              </p>
            </span>
            
            )}
          </p>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="bg-[#983b10] text-white py-2 px-6 rounded-md shadow hover:bg-[#702704]"
          >
            {isExpanded ? "Read Less" : "Read More"}
          </button>
          <img src="/images/screenshot-biblereading.png" alt="App Screenshot" className="mx-auto mt-10 mb-2 rounded-lg shadow-lg max-w-full sm:max-w-md w-full h-auto" />
        </div>
      </section>

      {/* Key Features Section */}
      <section className="bg-gray-200 py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Key Features</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              title="Bible Version Comparison"
              description="Compare translations side-by-side to gain deeper understanding."
              icon="📖"
            />
            <FeatureCard
              title="Verse Memos"
              description="Add personal reflections or sermon memos to specific verses."
              icon="📝"
            />
            <FeatureCard
              title="Highlight & Bookmark"
              description="Never lose track of your favorite passages."
              icon="🔖"
            />
            <FeatureCard
              title="Sermon Writing"
              description="Prepare, save, and organize your sermons efficiently."
              icon="✍️"
            />
            <FeatureCard
              title="Sync Across Devices"
              description="Access your reading tools anytime, anywhere."
              icon="☁️"
            />
            <FeatureCard
              title="Easy Search"
              description="Quickly find specific verses or topics across multiple versions."
              icon="🔍"
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-6">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <StepCard step="1" description="Sign up for free and log in." />
            <StepCard step="2" description="Search or select a Bible version." />
            <StepCard step="3" description="Add notes, highlights, or start a sermon." />
            <StepCard step="4" description="Save and share your insights." />
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <section className="bg-[#cca63d] text-white text-center py-12">
        <h2 className="text-3xl font-bold mb-4">Ready to start your journey?</h2>
        <OpenModalButton
          buttonText={
            <span className="bg-white text-blue-600 font-semibold py-2 px-6 rounded shadow hover:bg-gray-200">
              Sign Up for Free
            </span>
          }
          modalComponent={<SignupForm />}
        />
      </section>
    </div>
  );
};

const FeatureCard = ({ title, description, icon }) => (
  <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg text-center">
    <div className="text-4xl mb-4">{icon}</div>
    <h3 className="text-xl font-bold mb-2">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

const StepCard = ({ step, description }) => (
  <div className="bg-gray-100 p-6 rounded-lg shadow hover:shadow-lg text-center">
    <div className="text-blue-600 text-4xl font-bold mb-4">{step}</div>
    <p className="text-gray-700">{description}</p>
  </div>
);

export default AboutApp;
